import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '../../components/Icon.jsx';
import Modal from '../../components/Modal.jsx';
import { useT, useLang, t as translate } from '../../lib/i18n.jsx';
import { get, post, del, patch } from '../../lib/api.js';
import { useAsync, initials } from '../../lib/hooks.js';
import { formatDate, formatMonthly, relativeTime } from '../../lib/format.js';
import { errorMessage } from '../../lib/errors.js';
import { toast } from '../../lib/toast.jsx';
import { ConfirmDialog, DetailState, FormField, InlineError } from './detailUi.jsx';
import { hostingState } from '../../lib/status.js';

export default function HostingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const t = useT();
  const { lang } = useLang();

  const { data: hosting, loading, error, reload } = useAsync(() => get(`/hostings/${id}`), [id]);
  const [tab, setTab] = useState('overview');

  if (loading || error || !hosting) {
    return (
      <DetailState
        lang={lang}
        loading={loading}
        error={error}
        backLabel={t('hd.back')}
        onBack={() => navigate('/hosting')}
        notFoundTitle={t('hostingDetail.notFound')}
        onRetry={reload}
      />
    );
  }

  const server = hosting.server?.host || '—';
  const runtimeLabel = hosting.image?.label || hosting.runtime || '—';
  const state = hostingState(hosting, lang);

  return (
    <main className="page" data-screen-label={`Hosting: ${hosting.domain}`}>
      <button className="back-link" onClick={() => navigate('/hosting')}>
        <Icon name="arrow-left" size={14} />
        <span style={{ marginLeft: 4 }}>{t('hd.back')}</span>
      </button>

      <div className="detail-head">
        <div className="fav-lg">{initials(hosting.domain.split('.')[0])}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ overflowWrap: 'anywhere' }}>{hosting.domain}</h1>
          <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className={`status-pill ${state.tone}`.trim()} title={state.message || undefined}>
              <span className="dot" />
              {state.label}
            </span>
            {hosting.status === 'active' && hosting.health_checked_at && (
              <span style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>
                {t('health.checked', { time: relativeTime(hosting.health_checked_at, lang) })}
              </span>
            )}
            <span style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>
              {hosting.package?.name || '—'} · <span className="mono">{server}</span> · {runtimeLabel}
            </span>
          </div>
        </div>
      </div>

      <div className="tabs" role="tablist">
        {[
          ['overview', t('hd.tab.overview')],
          ['access', t('hostingDetail.tab.access')],
          ['db', t('hd.tab.db')],
          ['settings', t('hd.tab.settings')],
        ].map(([k, label]) => (
          <button key={k} role="tab" aria-selected={tab === k} aria-current={tab === k ? 'page' : undefined} onClick={() => setTab(k)}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <Overview hosting={hosting} lang={lang} t={t} onChanged={reload} />}
      {tab === 'access' && <AccessTab hosting={hosting} lang={lang} t={t} />}
      {tab === 'db' && <DatabaseTab hosting={hosting} lang={lang} t={t} />}
      {tab === 'settings' && <SettingsTab hosting={hosting} lang={lang} t={t} reload={reload} />}
    </main>
  );
}

// ─────────────────────────────────────────────────────────────
// Overview tab

function Overview({ hosting, lang, t, onChanged }) {
  const pkg = hosting.package;
  const selfSigned = hosting.ssl_enabled && hosting.ssl_provider === 'selfsigned';
  const [sslBusy, setSslBusy] = useState(false);

  const enableSsl = async () => {
    setSslBusy(true);
    try {
      await post(`/hostings/${hosting.id}/enable-ssl`, null);
      toast(t('hostingDetail.ssl.settingUp'));
      onChanged?.();
    } catch (e) {
      toast(errorMessage(e, { lang }));
    } finally {
      setSslBusy(false);
    }
  };

  return (
    <div className="dash-grid">
      <section className="card">
        <div className="card-h"><h3>{t('svc.package')}</h3></div>
        <div style={{ padding: '6px 22px 16px' }}>
          <Kv k={t('svc.package')} v={pkg?.name || '—'} />
          {pkg?.price_monthly != null && <Kv k={t('hostingDetail.price')} v={formatMonthly(pkg.price_monthly, lang)} />}
          {pkg?.disk_limit && <Kv k={t('hostingDetail.storage')} v={pkg.disk_limit} />}
          {pkg?.memory_limit && <Kv k={t('hostingDetail.memory')} v={pkg.memory_limit} />}
          {pkg?.cpu_limit && <Kv k="CPU" v={pkg.cpu_limit} />}
          <Kv k={t('hostingDetail.runtime')} v={hosting.image?.label || hosting.runtime || '—'} />
          {(hosting.addons || []).length > 0 && (
            <Kv k={t('hostingDetail.addons')} v={hosting.addons.map((a) => a.label).join(', ')} />
          )}
          <Kv k={t('hostingDetail.created')} v={formatDate(hosting.created_at, lang) || '—'} />
          <Kv k={t('hostingDetail.nextInvoice')} v={formatDate(hosting.next_billing_date, lang) || '—'} />
        </div>
      </section>

      <aside className="side-stack">
        <section className="card">
          <div className="card-h"><h3>{t('hd.access')}</h3></div>
          <div style={{ padding: '6px 22px 16px' }}>
            <Kv k={t('hd.access.host')} v={hosting.server?.host || '—'} mono />
            <Kv k={t('hd.access.port')} v="22 (SFTP)" mono />
            <Kv k={t('hostingDetail.lastChanged')} v={relativeTime(hosting.updated_at, lang) || '—'} />
          </div>
        </section>
        <section className="card">
          <div className="card-h"><h3>{t('host.spec.ssl')}</h3></div>
          <div style={{ padding: '6px 22px 16px' }}>
            <Kv
              k={t('hostingDetail.ssl.provider')}
              v={!hosting.ssl_enabled ? '—' : selfSigned ? t('hostingDetail.ssl.selfsigned') : "Let's Encrypt"}
            />
            <Kv
              k={t('hostingDetail.ssl.status')}
              v={!hosting.ssl_enabled ? t('hostingDetail.ssl.inactive')
                : selfSigned ? t('hostingDetail.ssl.temporary') : t('hostingDetail.ssl.active')}
            />
            {selfSigned && (
              <p style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.5 }}>
                {t('hostingDetail.ssl.selfsignedHint')}
              </p>
            )}
            {!hosting.ssl_enabled && hosting.status === 'active' && (
              <div style={{ marginTop: 12 }}>
                <button className="btn-ghost" onClick={enableSsl} disabled={sslBusy}>
                  {sslBusy ? t('detailUi.working') : t('hostingDetail.ssl.enable')}
                </button>
              </div>
            )}
          </div>
        </section>
      </aside>
    </div>
  );
}

function Kv({ k, v, mono }) {
  return (
    <div className="kv">
      <span className="k">{k}</span>
      <span className={'v ' + (mono ? 'mono' : '')} style={{ overflowWrap: 'anywhere' }}>{v}</span>
    </div>
  );
}

function copy(text, lang) {
  if (navigator.clipboard) {
    navigator.clipboard.writeText(text).then(
      () => toast(translate(lang, 'hostingDetail.copied')),
      () => toast(translate(lang, 'hostingDetail.copyFailed')),
    );
  }
}

// ─────────────────────────────────────────────────────────────
// SFTP access tab — GET/POST/DELETE /hostings/{id}/sftp-users, PATCH …/password

function AccessTab({ hosting, lang, t }) {
  const { data, loading, error, reload } = useAsync(() => get(`/hostings/${hosting.id}/sftp-users`), [hosting.id]);
  const [modal, setModal] = useState(null);
  const [secret, setSecret] = useState(null); // { username, password } shown once

  const host = data?.host || hosting.server?.host || '—';
  const port = data?.port || 22;
  const users = data?.users || [];
  const atLimit = data?.max_users != null && users.length >= data.max_users;

  return (
    <div className="det-grid">
      <section className="card">
        <div className="card-h"><h3>{t('hd.ssh.cred')}</h3></div>
        <div style={{ padding: '6px 22px 18px' }}>
          <Kv k={t('hd.access.host')} v={host} mono />
          <Kv k={t('hd.access.port')} v={String(port)} mono />
          <Kv k={t('hostingDetail.protocol')} v="SFTP" />
          {users[0] && (
            <div className="code-block" style={{ marginTop: 12 }}>
              <span className="mono" style={{ overflowWrap: 'anywhere' }}>{`sftp -P ${port} ${users[0].username}@${host}`}</span>
              <button className="btn-ghost btn-ghost--sm" type="button" onClick={() => copy(`sftp -P ${port} ${users[0].username}@${host}`, lang)}>
                {t('hostingDetail.copy')}
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="card">
        <div className="card-h">
          <h3>{t('hostingDetail.sftp.users')}</h3>
          <button
            className="btn-ghost"
            onClick={() => setModal({ kind: 'add' })}
            disabled={!hosting.server || atLimit}
            title={atLimit ? t('hostingDetail.sftp.limit', { max: data.max_users }) : undefined}
          >
            {t('hostingDetail.sftp.addUser')} +
          </button>
        </div>
        {atLimit && (
          <p style={{ margin: '0 22px 12px', fontSize: 12.5, color: 'var(--ink-3)' }}>
            {t('hostingDetail.sftp.limit', { max: data.max_users })}
          </p>
        )}
        {loading && <div style={{ padding: '14px 22px', color: 'var(--ink-3)', fontSize: 13 }}>{t('common.loading')}</div>}
        {error && <div style={{ padding: '0 22px 14px' }}><InlineError error={error} lang={lang} /></div>}
        {!loading && !error && users.length === 0 && (
          <div style={{ padding: '14px 22px 18px', color: 'var(--ink-3)', fontSize: 13 }}>
            {hosting.server
              ? t('hostingDetail.sftp.empty')
              : t('hostingDetail.sftp.noServer')}
          </div>
        )}
        <div className="keys-list">
          {users.map((u) => (
            <div className="key-row" key={u.id}>
              <span className="key-row__icon"><Icon name="user" size={14} /></span>
              <div className="key-row__body">
                <div className="key-row__label mono">{u.username}</div>
                <div className="key-row__fp">{u.created_at ? t('hostingDetail.sftp.createdOn', { date: formatDate(u.created_at, lang) }) : ''}</div>
              </div>
              <button className="btn-ghost btn-ghost--sm" onClick={() => setModal({ kind: 'reset', user: u })}>
                {t('hostingDetail.sftp.newPassword')}
              </button>
              <button className="icon-btn" onClick={() => setModal({ kind: 'delete', user: u })} title={t('hostingDetail.delete')} aria-label={t('hostingDetail.sftp.deleteUser', { name: u.username })}>
                <Icon name="trash" size={14} />
              </button>
            </div>
          ))}
        </div>
      </section>

      {modal?.kind === 'add' && (
        <AddSftpUserModal
          hosting={hosting}
          lang={lang}
          onClose={() => setModal(null)}
          onCreated={(res) => {
            setModal(null);
            setSecret({ username: res.username, password: res.password });
            reload();
          }}
        />
      )}
      {modal?.kind === 'reset' && (
        <ConfirmDialog
          danger={false}
          lang={lang}
          title={t('hostingDetail.sftp.reset.title')}
          body={t('hostingDetail.sftp.reset.body', { name: modal.user.username })}
          cta={t('hostingDetail.sftp.reset.cta')}
          onClose={() => setModal(null)}
          onConfirm={async () => {
            const res = await patch(`/hostings/${hosting.id}/sftp-users/${modal.user.id}/password`, null);
            setModal(null);
            setSecret({ username: res.username, password: res.password });
          }}
        />
      )}
      {modal?.kind === 'delete' && (
        <ConfirmDialog
          lang={lang}
          title={t('hostingDetail.sftp.delete.title')}
          body={t('hostingDetail.sftp.delete.body', { name: modal.user.username })}
          cta={t('hostingDetail.delete')}
          onClose={() => setModal(null)}
          onConfirm={async () => {
            await del(`/hostings/${hosting.id}/sftp-users/${modal.user.id}`);
            setModal(null);
            toast(t('hostingDetail.sftp.deleted'));
            reload();
          }}
        />
      )}
      {secret && <SecretModal lang={lang} username={secret.username} password={secret.password} onClose={() => setSecret(null)} />}
    </div>
  );
}

function AddSftpUserModal({ hosting, lang, onClose, onCreated }) {
  const t = useT();
  const [username, setUsername] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const valid = !username || /^[a-z][a-z0-9_-]{2,31}$/.test(username);

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await post(`/hostings/${hosting.id}/sftp-users`, username ? { username } : {});
      onCreated(res);
    } catch (e2) {
      setErr(e2);
      setBusy(false);
    }
  };

  return (
    <Modal
      as="form"
      onSubmit={submit}
      onClose={onClose}
      maxWidth={460}
      title={t('hostingDetail.sftp.add.title')}
      description={t('hostingDetail.sftp.add.description')}
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>{t('g.cancel')}</button>
          <button type="submit" className="btn-primary" disabled={busy || !valid}>
            {busy ? t('detailUi.working') : t('newPlan.create')}
          </button>
        </>
      }
    >
      <div className="modal-body">
        <InlineError error={err} lang={lang} />
        <FormField
          label={t('hostingDetail.sftp.add.username')}
          hint={t('hostingDetail.sftp.add.usernameHint')}
          error={!valid ? t('hostingDetail.sftp.add.invalid') : ''}
        >
          <input value={username} onChange={(e) => setUsername(e.target.value.trim())} autoComplete="off" spellCheck={false} className="mono" />
        </FormField>
      </div>
    </Modal>
  );
}

function SecretModal({ lang, username, password, onClose }) {
  const t = useT();
  return (
    <Modal
      onClose={onClose}
      maxWidth={480}
      title={t('hostingDetail.secret.title')}
      description={t('hostingDetail.secret.description')}
      footer={<button type="button" className="btn-primary" onClick={onClose}>{t('hostingDetail.secret.done')}</button>}
    >
      <div className="modal-body">
        <Kv k={t('hostingDetail.user')} v={username} mono />
        <div className="code-block" style={{ marginTop: 8 }}>
          <span className="mono" style={{ overflowWrap: 'anywhere' }}>{password}</span>
          <button className="btn-ghost btn-ghost--sm" type="button" onClick={() => copy(password, lang)}>
            {t('hostingDetail.copy')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────
// Database tab — GET /hostings/{id}/credentials

function DatabaseTab({ hosting, lang, t }) {
  const { data, loading, error } = useAsync(() => get(`/hostings/${hosting.id}/credentials`), [hosting.id]);
  const [reveal, setReveal] = useState(false);
  const db = data?.credentials?.database;
  const adminer = data?.credentials?.adminer;

  return (
    <section className="card">
      <div className="card-h">
        <h3>{t('ht.db.title')}</h3>
        {adminer?.url && (
          <a className="btn-ghost" href={adminer.url} target="_blank" rel="noopener noreferrer">
            {t('hostingDetail.db.adminer')} →
          </a>
        )}
      </div>
      <div style={{ padding: '6px 22px 18px' }}>
        {loading && <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>{t('common.loading')}</div>}
        <InlineError error={error} lang={lang} />
        {!loading && !error && !db && (
          <div style={{ color: 'var(--ink-3)', fontSize: 13, padding: '8px 0' }}>
            {t('hostingDetail.db.none')}
          </div>
        )}
        {db && (
          <>
            <Kv k={t('hostingDetail.db.type')} v={db.type === 'postgres' ? 'PostgreSQL' : 'MySQL'} />
            <Kv k={t('hostingDetail.db.database')} v={db.database} mono />
            <Kv k={t('hostingDetail.user')} v={db.username} mono />
            <Kv k={t('hostingDetail.db.host')} v={`${db.host}:${db.port}`} mono />
            <Kv
              k={t('hostingDetail.db.password')}
              v={
                <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span className="mono">{reveal ? db.password : '••••••••••••'}</span>
                  <button type="button" className="btn-ghost btn-ghost--sm" onClick={() => setReveal((v) => !v)}>
                    {reveal ? t('hostingDetail.hide') : t('hostingDetail.show')}
                  </button>
                  <button type="button" className="btn-ghost btn-ghost--sm" onClick={() => copy(db.password, lang)}>
                    {t('hostingDetail.copy')}
                  </button>
                </span>
              }
            />
          </>
        )}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Settings tab — POST restart, cancel, cancel/revoke

function SettingsTab({ hosting, lang, t, reload }) {
  const [confirm, setConfirm] = useState(null);
  const pendingCancel = hosting.status === 'pending_cancellation';

  return (
    <div className="det-grid">
      <section className="card">
        <div className="card-h"><h3>{t('hd.set.php.title')}</h3></div>
        <div style={{ padding: '6px 22px 18px' }}>
          <Kv k={t('hostingDetail.runtime.current')} v={hosting.image?.label || hosting.runtime || '—'} />
          <p style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.5 }}>
            {t('hostingDetail.runtime.change')}
          </p>
        </div>
      </section>

      <section className="card">
        <div className="card-h"><h3>{t('hd.set.restart.title')}</h3></div>
        <div style={{ padding: '6px 22px 18px' }}>
          <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.5 }}>{t('hd.set.restart.sub')}</p>
          <button className="btn-ghost" onClick={() => setConfirm('restart')} disabled={hosting.status !== 'active'}>
            {t('hd.set.restart.cta')}
          </button>
        </div>
      </section>

      <section className="card danger-card" style={{ gridColumn: '1 / -1' }}>
        <div className="card-h"><h3>{t('hd.set.danger.title')}</h3></div>
        <div style={{ padding: '6px 22px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.5, maxWidth: 520 }}>
            {pendingCancel
              ? hosting.next_billing_date
                ? t('hostingDetail.cancel.pendingUntil', { date: formatDate(hosting.next_billing_date, lang) })
                : t('hostingDetail.cancel.pendingEndOfPeriod')
              : t('hostingDetail.cancel.info')}
          </p>
          {pendingCancel ? (
            <button className="btn-ghost" onClick={() => setConfirm('revoke')}>
              {t('hostingDetail.revoke.button')}
            </button>
          ) : (
            <button className="btn-danger" onClick={() => setConfirm('cancel')} disabled={hosting.status === 'terminated'}>
              {t('hd.set.danger.cta')}
            </button>
          )}
        </div>
      </section>

      {confirm === 'restart' && (
        <ConfirmDialog
          danger={false}
          lang={lang}
          title={t('hostingDetail.restart.title')}
          body={t('hd.set.restart.sub')}
          cta={t('hd.set.restart.cta')}
          onClose={() => setConfirm(null)}
          onConfirm={async () => {
            await post(`/hostings/${hosting.id}/restart`, null);
            setConfirm(null);
            toast(t('hostingDetail.restart.started'));
          }}
        />
      )}
      {confirm === 'cancel' && (
        <ConfirmDialog
          lang={lang}
          title={t('hostingDetail.cancel.title')}
          body={t('hostingDetail.cancel.body', { domain: hosting.domain })}
          cta={t('hd.set.danger.cta')}
          onClose={() => setConfirm(null)}
          onConfirm={async () => {
            await post(`/hostings/${hosting.id}/cancel`, { reason: 'customer_request' });
            setConfirm(null);
            toast(t('hostingDetail.cancel.submitted'));
            reload();
          }}
        />
      )}
      {confirm === 'revoke' && (
        <ConfirmDialog
          danger={false}
          lang={lang}
          title={t('hostingDetail.revoke.title')}
          body={t('hostingDetail.revoke.body')}
          cta={t('hostingDetail.revoke.cta')}
          onClose={() => setConfirm(null)}
          onConfirm={async () => {
            await post(`/hostings/${hosting.id}/cancel/revoke`, null);
            setConfirm(null);
            toast(t('hostingDetail.revoke.done'));
            reload();
          }}
        />
      )}
    </div>
  );
}
